import React from 'react';
import { Button, Text } from '@ui';
import { FilePreviewThumb } from '@ui/molecules/FileHandler/FilePreviewThumb';
/** Sortable `dataKey`s contributed by the shared columns. A surface adds its own on top. */
export const BASE_SORTABLE_COLUMNS = ['name', 'typeLabel', 'uploadedSortValue', 'sizeBytes'];
function PreviewCell({ item, onPreviewClick }) {
    const thumbKind = item.previewKind === 'photo' || item.previewKind === 'pdf' ? item.previewKind : 'other';
    return (React.createElement("button", { type: "button", className: "attachments-surface__preview-cell", "aria-label": `Preview ${item.name}`, onClick: (event) => {
            event.stopPropagation();
            onPreviewClick(item);
        } },
        React.createElement(FilePreviewThumb, { kind: thumbKind, previewUrl: item.url, downloadUrl: item.url })));
}
/**
 * The columns every attachment surface shares, with the surface's own columns spliced in after
 * Name. Sort comparators read `dataKey` off the row (Table's own client-side sort), so columns
 * whose display differs from their sort value (Uploaded, Size) point `dataKey` at a derived
 * numeric field and use a `renderer` for the formatted display — Size in particular must sort as
 * bytes, never as the formatted string ("640 KB" must not sort above "24.6 MB").
 */
export function buildBaseColumns({ t, onPreviewClick, onRequestRowDelete, renderNameCell, rowDeleteAriaLabel, entityColumns, renderRowAction, hideRowAction = false }) {
    const columns = [
        {
            // Pinned with the selection checkbox and Name so the row stays identifiable while the
            // table scrolls horizontally (per the signed-off design). `width` is mandatory on every
            // pinned column except the last — Name is last, so it stays fluid. The action column is
            // deliberately NOT pinned.
            dataKey: 'preview',
            pinned: true,
            label: t('columns.preview'),
            width: '64px',
            renderer: ({ item }) => React.createElement(PreviewCell, { item: item, onPreviewClick: onPreviewClick })
        },
        {
            dataKey: 'name',
            label: t('columns.name'),
            columnType: 'primary',
            pinned: true,
            renderer: ({ item }) => renderNameCell(item)
        },
        ...entityColumns,
        {
            dataKey: 'typeLabel',
            label: t('columns.type'),
            width: '72px'
        },
        {
            dataKey: 'uploadedSortValue',
            label: t('columns.uploaded'),
            renderer: ({ item }) => React.createElement(Text, { variant: "body" }, item.uploadedLabel)
        },
        {
            dataKey: 'sizeBytes',
            label: t('columns.size'),
            renderer: ({ item }) => React.createElement(Text, { variant: "body" }, item.sizeLabel)
        }
    ];
    // Omitted outright when nothing on the row may be mutated, rather than rendered empty: an empty
    // action column still reserves its width and reads as a control that failed to load.
    if (!hideRowAction) {
        columns.push({
            dataKey: 'actions',
            label: '',
            columnType: 'action',
            renderer: ({ item }) => renderRowAction ? (renderRowAction(item)) : (React.createElement(Button, { "aria-label": rowDeleteAriaLabel(item.name), onClick: (event) => {
                    event.stopPropagation();
                    onRequestRowDelete(item);
                } },
                React.createElement("i", { className: "fa-light fa-trash", "aria-hidden": "true" })))
        });
    }
    return columns;
}
